import { useState } from "react";
import { useLocation } from "wouter";
import { ShieldCheck, Palette, Globe, Trash2 } from "lucide-react";
import { usePlatform } from "@/lib/platform";
import { ContactDetailsSection } from "../components/ContactDetailsSection";
import DeleteAccountModal from "../components/DeleteAccountModal";
import { AccountRow } from "../components/AccountRow";
import { ThemeToggle } from "@/components/ux/ThemeToggle";
import { LanguageSelector, useT } from "@/lib/i18n";
import { PageHeader } from "@/components/ux/PageHeader";
import { ManageCookiesLink } from "@/components/consent/ManageCookiesLink";

// The set-once configuration, reached from the gear or the Settings row on the
// account page: contact details, app preferences, legal, and account deletion.
// Everything a member uses regularly (tickets, invites, groups, notifications,
// help, sign out) lives on the account page itself.
export default function Settings() {
  const { t } = useT();
  const [, navigate] = useLocation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { isNative, platform } = usePlatform();
  const isAppleNative = isNative && platform === "ios";

  return (
    <div className="pt-4 pb-24 md:pt-8 px-4">
      <div className="max-w-2xl mx-auto">
        <PageHeader title="Settings" onBack={() => navigate("/profile")} />

        <div className="space-y-2">
          {/* Contact details — phone & Instagram */}
          <ContactDetailsSection />

          {/* Preferences */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="w-5 h-5 text-primary-500" />
              <h2 className="text-xl font-semibold dark:text-neutral-100">Preferences</h2>
            </div>

            <div className="flex flex-col">
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-2 text-sm font-medium dark:text-neutral-200">
                  <Globe className="w-4 h-4 text-primary-500" />
                  Language
                </div>
                <LanguageSelector className="dark:text-neutral-100 dark:border-neutral-600" />
              </div>
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-2 text-sm font-medium dark:text-neutral-200">
                  <Palette className="w-4 h-4 text-primary-500" />
                  Theme
                </div>
                <ThemeToggle />
              </div>
            </div>
          </div>

          {/* Legal */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-5 h-5 text-primary-500" />
              <h2 className="text-xl font-semibold dark:text-neutral-100">Legal</h2>
            </div>

            <div className="flex flex-col gap-3">
              <a href="/terms" className="flex items-center py-2 transition-colors primary-link">
                {t("profile.legal.terms")}
              </a>
              {isAppleNative && (
                <a
                  href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                  target="_blank"
                  className="flex items-center py-2 transition-colors primary-link"
                >
                  {t("profile.legal.appleTerms")}
                </a>
              )}
              <a href="/privacy" className="flex items-center py-2 transition-colors primary-link">
                {t("profile.legal.privacy")}
              </a>
              <a
                href="/community-guidelines"
                className="flex items-center py-2 transition-colors primary-link"
              >
                {t("profile.legal.communityGuidelines")}
              </a>
              <ManageCookiesLink className="flex items-center py-2 transition-colors primary-link">
                {t("profile.legal.manageCookies")}
              </ManageCookiesLink>
            </div>
          </div>

          <AccountRow
            icon={Trash2}
            title={t("profile.account.deleteAccount")}
            tone="danger"
            onClick={() => setIsDeleteModalOpen(true)}
          />
        </div>
      </div>

      <DeleteAccountModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} />
    </div>
  );
}
